import React from "react";
import MuiIconButton, { IconButtonProps as MuiIconButtonProps } from "@mui/material/IconButton";
import type { SxProps, Theme } from "@mui/material/styles";
import styles from "./CustomIconButton.module.css";

export type CustomIconButtonColor =
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "warning"
  | "info"
  | "success"
  | "inherit"
  | "inheritWhite";

export type CustomIconButtonSize = "small" | "medium" | "large";

export type CustomIconButtonState =
  | "enabled"
  | "hovered"
  | "focused"
  | "pressed"
  | "disabled";

export interface CustomIconButtonProps
  extends Omit<MuiIconButtonProps, "color" | "size" | "sx"> {
  color?: CustomIconButtonColor;
  size?: CustomIconButtonSize;
  state?: CustomIconButtonState;
  sx?: SxProps<Theme>;
}

const joinClassNames = (...classNames: Array<string | undefined | null | false>) =>
  classNames.filter(Boolean).join(" ");

const COLOR_CLASS_MAP: Record<CustomIconButtonColor, string> = {
  default: styles.colorDefault,
  primary: styles.colorPrimary,
  secondary: styles.colorSecondary,
  error: styles.colorError,
  warning: styles.colorWarning,
  info: styles.colorInfo,
  success: styles.colorSuccess,
  inherit: styles.colorInherit,
  inheritWhite: styles.colorInheritWhite,
};

const SIZE_CLASS_MAP: Record<CustomIconButtonSize, string> = {
  small: styles.sizeSmall,
  medium: styles.sizeMedium,
  large: styles.sizeLarge,
};

const STATE_CLASS_MAP: Partial<Record<CustomIconButtonState, string>> = {
  hovered: styles.stateHovered,
  focused: styles.stateFocused,
  pressed: styles.statePressed,
  disabled: styles.stateDisabled,
};

const toMuiColor = (
  color: CustomIconButtonColor,
): Exclude<MuiIconButtonProps["color"], undefined> => {
  if (color === "inheritWhite") {
    return "inherit";
  }

  return color;
};

export const CustomIconButton = ({
  color = "default",
  size = "medium",
  state = "enabled",
  disabled,
  className,
  sx,
  ...props
}: CustomIconButtonProps) => {
  const resolvedState: CustomIconButtonState =
    disabled || state === "disabled" ? "disabled" : state;

  return (
    <MuiIconButton
      {...props}
      size={size}
      color={toMuiColor(color)}
      disabled={resolvedState === "disabled"}
      sx={sx}
      className={joinClassNames(
        styles.root,
        COLOR_CLASS_MAP[color],
        SIZE_CLASS_MAP[size],
        STATE_CLASS_MAP[resolvedState],
        className,
      )}
    />
  );
};

export default CustomIconButton;
